import {
  CompRef,
  CompStructure,
  PageRef,
  Point,
  WidgetDescriptor,
} from '@wix/document-services-types';
import { chainPromisesAsync } from '../utils';
import {
  AddToStageData,
  UnifiedComponentsCollection,
  UnifiedPage,
  UnifiedWidget,
  WidgetAsContent,
  AppData,
} from '../../../../../types/unifiedComponents';
import {
  createAddUnifiedComponentsError,
  AddUnifiedComponentsErrorCode as ErrorType,
} from '../errors';
import { ExtendedPlatformContext } from '../../../../../types/platformApi';
import {
  BLOCKS_WIDGET_COMP_TYPE,
  CUSTOM_ELEMENT_COMP_TYPE,
  PAGE_REGIONS,
} from '../constants';
import {
  addBlocksWidgetResolver,
  getBlocksStructure,
  getWidgetAppearanceData,
} from './addBlocksWidget';
import {
  addCustomElement,
  getCustomElementStructure,
} from './addCustomElement';
import { Item } from '@wix/editor-platform-host-integration-apis';

const TOKEN = 'unified-components-installation';

const addWidgetHandler = {
  [BLOCKS_WIDGET_COMP_TYPE]: addBlocksWidgetResolver,
  [CUSTOM_ELEMENT_COMP_TYPE]: addCustomElement,
};

const getStructureHandler = {
  [BLOCKS_WIDGET_COMP_TYPE]: (
    context: ExtendedPlatformContext,
    { appDefinitionId }: AppData,
    widgetDefinition: UnifiedWidget,
    { id: presetId }: Omit<Item, 'addToStage' | 'getStructure'>,
    position: Point,
    widgetDescriptor: Pick<WidgetDescriptor, 'defaultSize' | 'presets'>,
  ) =>
    getBlocksStructure(
      context,
      appDefinitionId,
      widgetDefinition,
      presetId,
      position,
      widgetDescriptor,
    ),
  [CUSTOM_ELEMENT_COMP_TYPE]: (
    _context: ExtendedPlatformContext,
    { appDefinitionId }: AppData,
    { widgetId }: UnifiedWidget,
    { size }: Omit<Item, 'addToStage' | 'getStructure'>,
    position: Point,
    _widgetDescriptor: Pick<WidgetDescriptor, 'defaultSize' | 'presets'>,
  ) => getCustomElementStructure(appDefinitionId, widgetId, size, position),
};

type AllowedWidget = keyof typeof addWidgetHandler;

export function getAddUnifiedComponentsMethods(
  context: ExtendedPlatformContext,
  appData: AppData,
) {
  function createBlankPage(pageDefinition: UnifiedPage) {
    return new Promise<PageRef>((resolve, reject) => {
      const pageUriSEO = pageDefinition.installation?.page?.slug
        ?.split('/')
        .pop();
      if (!pageUriSEO) {
        // TODO: Should we create logic that define a default?
        reject(
          createAddUnifiedComponentsError(
            ErrorType.pageHaveNoSlugDefinition,
            `The page ${pageDefinition.base?.name} have no slug definition`,
          ),
        );
      }
      const definition = {
        data: {
          tpaPageId: pageDefinition.base?.id,
          pageUriSEO,
          appDefinitionId: appData.appDefinitionId,
          managingAppDefId: appData.appDefinitionId,
        },
      };

      try {
        // Should use pages.add of DS instead - once studio migrate their logic to be covered in DS.
        context.platformApiMethods.document.pages
          .add(appData, TOKEN, {
            definition,
            title: pageDefinition.base?.name as string,
            shouldAddMenuItem:
              pageDefinition.installation?.page?.addToSiteMenu ?? false,
            shouldNavigateToPage: false,
          })
          .then((pageRef) =>
            context.documentServices.waitForChangesApplied(() =>
              resolve(pageRef),
            ),
          );
      } catch (error) {
        reject(error);
      }
    });
  }

  function addPage(
    pageDefinition: UnifiedPage,
    widgets: UnifiedComponentsCollection['widgets'],
  ): Promise<PageRef | undefined> {
    return new Promise((resolve, reject) => {
      if (!pageDefinition.installation?.base?.autoAdd) {
        return resolve(undefined);
      }

      // TODO: Do we want to throw error or to generate alternative slug?
      createBlankPage(pageDefinition)
        .then((thisPageRef) =>
          addContentToPage(pageDefinition, widgets, thisPageRef),
        )
        .then(resolve)
        .catch((error: Error) =>
          reject(
            createAddUnifiedComponentsError(
              ErrorType.creatingNewPageFailed,
              `Failed to create ${pageDefinition.base?.id} page`,
            )
              .withUserComponentId(
                pageDefinition.base?.id as string,
                'unified-page',
              )
              .withParentError(error as Error),
          ),
        );
    });
  }

  function addContentToPage(
    pageDefinition: UnifiedPage,
    widgets: UnifiedComponentsCollection['widgets'],
    pageRef: PageRef,
  ): Promise<PageRef> {
    const addWidgetAsContent = (widget: WidgetAsContent) =>
      new Promise((resolve, reject) => {
        const { widgetGuid, preset } = widget;
        const widgetDefinition = widgets[widgetGuid as string];
        if (!widgetDefinition) {
          return reject(
            createAddUnifiedComponentsError(
              ErrorType.widgetGuidNotFound,
              `Could not find widget: ${widgetGuid} to install over page: ${pageDefinition.base?.name}`,
            ).withUserComponentId(widgetGuid as string, 'unified-widget'),
          );
        }

        addWidgetWithinNewSection(pageRef, widgetDefinition, {
          // TODO: [EP-4915] Should take presetId from widget if defined, and if not use preset object as fallback.
          //  presetId: widget.presetId ?? preset?.desktopPresetId
          presetId: preset?.desktopPresetId,
        })
          .then(resolve)
          .catch(reject);
      });

    return new Promise((resolve, reject) => {
      chainPromisesAsync(
        pageDefinition.content?.widgets ?? [],
        addWidgetAsContent,
      )
        .then(() => resolve(pageRef))
        .catch(reject);
    });
  }

  function addWidgetWithinNewSection(
    pageRef: PageRef,
    widgetDefinition: UnifiedWidget,
    addToStageData: AddToStageData,
  ): Promise<PageRef> {
    return new Promise((resolve, reject) => {
      context.platformApiMethods.editor.sections
        .addSectionToPage(
          appData,
          TOKEN,
          (newSectionRef: CompRef) =>
            addWidgetInSection(widgetDefinition, newSectionRef, addToStageData),
          { pageRef, sectionName: widgetDefinition?.base?.name },
        )
        .then(() => {
          resolve(pageRef);
        })
        .catch((error: Error) => {
          reject(
            createAddUnifiedComponentsError(
              ErrorType.addingWidgetIntoSectionFailed,
              'failed to add widget within new section',
            )
              .withPageRef(pageRef, true)
              .withUserComponentId(
                widgetDefinition?.base?.id ?? widgetDefinition?.widgetId,
                'unified-widget',
              )
              .withParentError(error as Error),
          );
        });
    });
  }

  // TODO: Do not add widget when it installed as 'maxInstances' times - trigger installation failure instead.
  function addWidgetInSection(
    widgetDefinition: UnifiedWidget,
    containerRef: CompRef,
    addToStageData: AddToStageData,
  ): Promise<CompRef> {
    return new Promise((resolve, reject) => {
      if (Object.keys(addWidgetHandler).includes(widgetDefinition.type)) {
        addWidgetHandler[widgetDefinition.type as AllowedWidget](
          context,
          appData,
          widgetDefinition,
          containerRef,
          addToStageData,
        )
          .then((widgetRef) => {
            context.documentServices.waitForChangesApplied(() =>
              resolve(widgetRef),
            );
          })
          .catch(reject);
      } else {
        const { widgetId } = widgetDefinition;
        reject(
          createAddUnifiedComponentsError(
            ErrorType.widgetTypeNotSupported,
            `There is no implementation to support adding ${widgetDefinition.type} widget`,
          ).withUserComponentId(widgetId, 'unified-widget'),
        );
      }
    });
  }

  function getStructure(
    widgetDefinition: UnifiedWidget,
    widgetDescriptor: Pick<WidgetDescriptor, 'defaultSize' | 'presets'>,
    presetData: Omit<Item, 'addToStage' | 'getStructure'>,
    position: Point = { x: 0, y: 0 },
  ): CompStructure {
    if (Object.keys(getStructureHandler).includes(widgetDefinition.type)) {
      return getStructureHandler[widgetDefinition.type as AllowedWidget](
        context,
        appData,
        widgetDefinition,
        presetData,
        position,
        widgetDescriptor,
      ) as CompStructure;
    } else {
      const { widgetId } = widgetDefinition;
      throw createAddUnifiedComponentsError(
        ErrorType.widgetTypeNotSupported,
        `There is no implementation to support getting structure of ${widgetDefinition.type} widget`,
      ).withUserComponentId(widgetId, 'unified-widget');
    }
  }

  function addWidget(pageRef: PageRef, widgetDefinition: UnifiedWidget) {
    return new Promise<PageRef | void>((resolve, reject) => {
      if (!widgetDefinition.installation?.base?.autoAdd) {
        return resolve();
      }

      const { region } = widgetDefinition.installation?.widget ?? {};

      if (region === PAGE_REGIONS.BODY) {
        addWidgetWithinNewSection(pageRef, widgetDefinition, {
          presetId:
            // TODO: [EP-4736] Use installation default preset ID once available.
            //  Replace widgetDefinition.presets?.[0].id With widget.installation?.widget?.defaultPresetId
            widgetDefinition.presets?.[0].id ??
            widgetDefinition.installation?.widget?.defaultPreset
              ?.desktopPresetId,
        })
          .then(resolve)
          .catch(reject);
      } else {
        resolve();
      }
    });
  }

  return { addPage, addWidget, addWidgetInSection, getStructure };
}
